import * as yup from 'yup';
import { ComisionistType, CreateComisionistRequest } from 'types/api/responses';
import { REQUIRED_DOCS_FORMAL, REQUIRED_DOCS_INFORMAL } from '../types/comisionist.types';

const DUI_REGEX = /^\d{8}-\d{1}$/;
const NIT_REGEX = /^\d{4}-\d{6}-\d{3}-\d{1}$/;
const NRC_REGEX = /^\d{6}-\d{1}$/;
const PHONE_REGEX = /^[0-9-]{8,15}$/;

export const getRequiredDocumentsByType = (type: ComisionistType): string[] =>
  type === 'Formal' ? [...REQUIRED_DOCS_FORMAL] : [...REQUIRED_DOCS_INFORMAL];

export const comisionistDataValidationSchema = yup.object({
  name: yup
    .string()
    .trim()
    .min(3, 'El nombre debe tener mínimo 3 caracteres')
    .max(150, 'El nombre debe tener máximo 150 caracteres')
    .required('El nombre es requerido'),
  type: yup
    .mixed<ComisionistType>()
    .oneOf(['Formal', 'Informal'], 'Tipo inválido')
    .required('El tipo es requerido'),
  dui: yup.string().when('type', {
    is: 'Informal',
    then: (schema) =>
      schema
        .trim()
        .required('El DUI es obligatorio para comisionistas informales')
        .matches(DUI_REGEX, 'Formato inválido (Ej: 12345678-9)'),
    otherwise: (schema) =>
      schema
        .trim()
        .transform((value) => value || undefined)
        .notRequired()
        .matches(DUI_REGEX, {
          message: 'Formato inválido (Ej: 12345678-9)',
          excludeEmptyString: true,
        }),
  }),
  nit: yup
    .string()
    .trim()
    .required('El NIT es requerido')
    .matches(NIT_REGEX, 'Formato inválido (Ej: 0614-112345-001-4)'),
  nrc: yup.string().when('type', {
    is: 'Formal',
    then: (schema) =>
      schema
        .trim()
        .required('El NRC es obligatorio para comisionistas formales')
        .min(2, 'El NRC debe tener al menos 2 caracteres')
        .max(20, 'El NRC no puede tener más de 20 caracteres'),
    otherwise: (schema) =>
      schema
        .trim()
        .transform(() => undefined)
        .test(
          'no-nrc-informal',
          'El NRC no aplica para comisionistas informales',
          (value) => !value || String(value).trim() === '',
        ),
  }),
  clientAccount: yup
    .string()
    .trim()
    .required('La cuenta AS400 es requerida'),
  branchOfficeId: yup.string().trim().required('La sucursal es requerida'),
  zoneId: yup
    .number()
    .typeError('La zona es requerida')
    .min(1, 'La zona es requerida')
    .required('La zona es requerida'),
  email: yup
    .string()
    .trim()
    .transform((value) => value || undefined)
    .email('Formato de email inválido')
    .notRequired(),
  phone: yup
    .string()
    .trim()
    .transform((value) => value || undefined)
    .matches(PHONE_REGEX, {
      message: 'Formato inválido (Ej: 2123-4567)',
      excludeEmptyString: true,
    })
    .notRequired(),
  isLargeContributor: yup.boolean().when('type', {
    is: 'Formal',
    then: (schema) =>
      schema
        .required('Indique si es gran contribuyente')
        .typeError('Indique si es gran contribuyente')
        .oneOf([true, false], 'Seleccione Sí o No'),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
});

export const comisionistDataInitialValues: CreateComisionistRequest = {
  name: '',
  type: 'Formal',
  dui: '',
  nit: '',
  nrc: '',
  branchOfficeId: '',
  zoneId: 0,
  email: '',
  phone: '',
  isLargeContributor: undefined,
  clientAccount: '',
  dateDischarge: '',
  verified: undefined,
};
